/**
 * The scene's black box (D81). When a phone's browser takes the tab away there is nothing left to read: no
 * console, no error, no report. So the scene writes down where it got to and what it was holding, in the one
 * place that survives the tab dying. The next time the game starts, a note that was never closed off means
 * the last session ended badly, and it is sent to the server so somebody can look at it.
 *
 * A note belongs to the session that wrote it (D96). Every report the live server had received said
 * "renderer created" and had been written a third of a second before it arrived: the colony's host wrote
 * its first note, and the app then read that very note back as a session that had ended badly — while the
 * real note of the session that died had already been overwritten. Only another session may find a note
 * unfinished, so the black box finally reports what it was built for.
 */
const KEY = "zend.scene.blackbox";

export interface SceneNote {
  /** The last thing the scene managed to do. */
  step: string;
  at: number;
  /** Which page load wrote the note; a session never reports its own. */
  session?: string;
  /** Where the player was, so a dead tab says which screen it died on. */
  route?: string;
  quality?: string;
  textureMb?: number;
  drawCalls?: number;
  triangles?: number;
  models?: number;
  pending?: number;
  fps?: number;
  /** How many times the context was lost in a row without the scene drawing in between (D87). */
  relapses?: number;
  /** Graphics objects the renderer is holding. */
  geometries?: number;
  textures?: number;
  programs?: number;
  /** WebGL contexts the page holds: the colony, the previews and the tactical field while it is open. */
  contexts?: number;
  /** What the tactical field was holding when it was open (D96). */
  field?: Record<string, number | string | boolean>;
  /** The unit a sheet was showing. */
  sheet?: string;
  /** What kind of machine this is. */
  device?: { memoryGb?: number; cores?: number; pixelRatio?: number; screen?: string; agent?: string };
  gpu?: string;
  maxTexture?: number;
  /** Set when the player left the page in the ordinary way; an unset flag means the tab died. */
  closed?: boolean;
}

function read(): SceneNote | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const note = JSON.parse(raw) as SceneNote;
    return typeof note?.step === "string" ? note : null;
  } catch {
    return null;
  }
}

function write(note: SceneNote): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(note));
  } catch {
    /* a browser that refuses storage simply has no black box */
  }
}

const sessionId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

let session = sessionId();
let current: SceneNote | null = null;

/** Test seam: what a page load does — the notes written from here on belong to a new session. */
export function startNewSession(): void {
  session = sessionId();
  current = null;
}

/** What kind of machine this is, asked once. */
export function describeDevice(gl?: WebGLRenderingContext | WebGL2RenderingContext | null): Pick<SceneNote, "device" | "gpu" | "maxTexture"> {
  const nav = typeof navigator !== "undefined" ? (navigator as Navigator & { deviceMemory?: number }) : undefined;
  const out: Pick<SceneNote, "device" | "gpu" | "maxTexture"> = {
    device: {
      ...(nav?.deviceMemory !== undefined ? { memoryGb: nav.deviceMemory } : {}),
      ...(nav?.hardwareConcurrency !== undefined ? { cores: nav.hardwareConcurrency } : {}),
      ...(typeof devicePixelRatio === "number" ? { pixelRatio: Math.round(devicePixelRatio * 100) / 100 } : {}),
      ...(typeof screen !== "undefined" ? { screen: `${screen.width}x${screen.height}` } : {}),
      ...(nav?.userAgent ? { agent: nav.userAgent.slice(0, 120) } : {}),
    },
  };
  if (gl) {
    try {
      const info = gl.getExtension("WEBGL_debug_renderer_info");
      if (info) out.gpu = String(gl.getParameter(info.UNMASKED_RENDERER_WEBGL)).slice(0, 80);
      out.maxTexture = Number(gl.getParameter(gl.MAX_TEXTURE_SIZE));
    } catch {
      /* a browser that hides its GPU tells us nothing, which is fine */
    }
  }
  return out;
}

/** Records where the scene is now. Cheap enough to call on every milestone and every few seconds. */
export function noteStep(step: string, extra: Partial<SceneNote> = {}): void {
  const route = typeof location !== "undefined" ? location.pathname : undefined;
  current = { ...(current ?? {}), ...extra, ...(route ? { route } : {}), step, at: Date.now(), session, closed: false };
  write(current);
}

/** The player left in the ordinary way, so the note is closed off and proves nothing went wrong. */
export function closeNote(): void {
  if (!current) return;
  current = { ...current, closed: true };
  write(current);
}

/**
 * A note from a previous session that was never closed off: the tab died while the scene was at that step.
 * Reading it clears it, so one bad session is reported once. This session's own open note is not a crash,
 * it is the session still running, and it stays where it is for a later session to find.
 */
export function takeUnfinishedNote(): SceneNote | null {
  const note = read();
  if (!note || note.closed) return null;
  if (note.session === session) return null;
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
  return note;
}
